import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2, Plus, Globe, Tag } from 'lucide-react'
import { sourcesApi } from '@/services/api'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { TagInput } from '@/components/ui/tag-input'

interface AddSourceDialogProps {
  trigger?: React.ReactNode
}

const INITIAL_FORM_STATE = {
  url: '',
  name: '',
  industry_tags: '',
}

export function AddSourceDialog({ trigger }: AddSourceDialogProps) {
  const [open, setOpen] = useState(false)
  const [formData, setFormData] = useState(INITIAL_FORM_STATE)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const queryClient = useQueryClient()

  const createMutation = useMutation({
    mutationFn: (data: typeof formData) => {
      const payload: { url: string; name?: string; industry_tags?: string[] } = {
        url: data.url.trim(),
      }
      const name = data.name.trim()
      if (name) payload.name = name
      // Parse industry_tags from comma-separated string
      const tags = data.industry_tags
        .split(',')
        .map((t) => t.trim())
        .filter((t) => t !== '')
      if (tags.length > 0) payload.industry_tags = tags
      return sourcesApi.create(payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sources'] })
      setOpen(false)
      resetForm()
    },
  })

  const resetForm = () => {
    setFormData(INITIAL_FORM_STATE)
    setErrors({})
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.url.trim()) {
      newErrors.url = 'URL is required'
    } else {
      try {
        const u = formData.url.trim()
        new URL(u.startsWith('http') ? u : `https://${u}`)
      } catch {
        newErrors.url = 'Please enter a valid URL'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (validateForm()) {
      createMutation.mutate(formData)
    }
  }

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev }
        delete next[field]
        return next
      })
    }
  }

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen)
    if (!newOpen) {
      resetForm()
      createMutation.reset()
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger || (
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            Add Source
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[420px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Add Data Source</DialogTitle>
            <DialogDescription>
              Add a sourcing site to your list. The system will analyze it to
              determine if we can scrape it. Name is optional and will be
              generated from the URL.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="url" className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-muted-foreground" />
                Source URL <span className="text-destructive">*</span>
              </Label>
              <Input
                id="url"
                type="url"
                placeholder="https://example.com/companies"
                value={formData.url}
                onChange={(e) => handleChange('url', e.target.value)}
                className={errors.url ? 'border-destructive' : ''}
              />
              {errors.url && (
                <p className="text-sm text-destructive">{errors.url}</p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="name">Source Name</Label>
              <Input
                id="name"
                placeholder="e.g., Europages (optional)"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Leave empty to auto-generate from URL
              </p>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="industry_tags" className="flex items-center gap-2">
                <Tag className="h-4 w-4 text-muted-foreground" />
                Industry Tags
              </Label>
              <TagInput
                id="industry_tags"
                value={formData.industry_tags}
                onChange={(value) => handleChange('industry_tags', value)}
                placeholder="e.g., fertilizer, agriculture"
                variant="outline"
              />
              <p className="text-xs text-muted-foreground">
                Add tags to categorize this source (press Enter or comma to add)
              </p>
            </div>
          </div>

          {createMutation.isError && (
            <div className="mb-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {createMutation.error instanceof Error
                ? createMutation.error.message
                : 'Failed to create source. Please try again.'}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={createMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Source
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
